<template>
  <div class="ip-group-editor">
    <div v-if="loading" class="text-muted small py-2">
      <i class="fas fa-spinner fa-spin me-1"></i>{{ $t("Loading...") }}
    </div>
    <template v-else>
      <div v-if="rows.length" class="table-responsive">
        <table class="table table-sm align-middle mb-1 ip-group-editor__table">
          <thead>
            <tr>
              <th v-for="sub in subFields" :key="'h-' + sub.key">{{ $t(sub.label) }}</th>
              <th class="ip-group-editor__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in rows" :key="row._id || row._tmpKey">
              <td v-for="sub in subFields" :key="(row._id || row._tmpKey) + '-' + sub.key">
                <ItemProfileRowContext
                  v-if="cellComponent(sub)"
                  :model-name="modelName"
                  :lists="lists"
                  :row="row"
                >
                  <component
                    :is="cellComponent(sub)"
                    :field="sub"
                    :value="row[sub.key]"
                    mode="edit"
                    :fieldId="`ip-${field.key}-${idx}-${sub.key}`"
                    :editingFields="sub.key"
                    :disableLock="true"
                    :lists="lists"
                    :record-item="row"
                    :parentItem="row"
                    :showLabel="false"
                    @input="(val) => setCell(idx, sub.key, val)"
                  />
                </ItemProfileRowContext>
                <input
                  v-else
                  class="form-control form-control-sm"
                  :value="row[sub.key]"
                  @change="setCell(idx, sub.key, $event.target.value)"
                />
              </td>
              <td class="ip-group-editor__actions">
                <button
                  type="button"
                  class="btn btn-sm btn-link text-danger px-1 mb-0"
                  :title="$t('Delete')"
                  @click="removeRow(idx)"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <button type="button" class="btn btn-sm btn-link px-0 mb-0" @click="addRow">
        <i class="fas fa-plus me-1"></i>
        {{ field.groupAddLineLabel ? $t(field.groupAddLineLabel) : $t("itemProfile.addRow") }}
      </button>
    </template>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import {
  FieldText,
  FieldNumber,
  FieldList,
  FieldReference,
  FieldDate,
  FieldBoolean,
  FieldTextList,
} from "@/components/gotrading/model-definition-core";
import ItemProfileRowContext from "./ItemProfileRowContext.vue";

const CELL_COMPONENTS = {
  string: "FieldText",
  text: "FieldText",
  email: "FieldText",
  textarea: "FieldText",
  number: "FieldNumber",
  list: "FieldList",
  reference: "FieldReference",
  date: "FieldDate",
  boolean: "FieldBoolean",
  textlist: "FieldTextList",
};

let tmpCounter = 0;

/** Lo que el back necesita de cada fila: su _id (si ya existe) y los subcampos. */
export function cleanGroupRows(rows, subFields) {
  return (rows || []).map((row) => {
    const out = {};
    if (row && row._id) out._id = String(row._id);
    for (const sub of subFields) {
      let v = row ? row[sub.key] : undefined;
      if (v && typeof v === "object" && !Array.isArray(v) && v._id != null) v = String(v._id);
      if (Array.isArray(v)) {
        v = v.map((x) => (x && typeof x === "object" && x._id != null ? String(x._id) : x));
      }
      out[sub.key] = v === undefined || v === "" ? null : v;
    }
    return out;
  }).filter(
    // Una fila agregada y dejada en blanco no se manda: el back la crearía vacía.
    (row) =>
      row._id ||
      subFields.some((sub) => {
        const v = row[sub.key];
        return v !== null && !(Array.isArray(v) && v.length === 0);
      })
  );
}

export default {
  name: "ItemProfileGroupEditor",
  components: {
    ItemProfileRowContext,
    FieldText,
    FieldNumber,
    FieldList,
    FieldReference,
    FieldDate,
    FieldBoolean,
    FieldTextList,
  },
  props: {
    field: { type: Object, required: true },
    value: { type: [Array, Object, String], default: null },
    modelName: { type: String, required: true },
    lists: { type: Array, default: () => [] },
  },
  data() {
    return {
      rows: [],
      loading: false,
    };
  },
  computed: {
    subFields() {
      return (this.field.fields || []).filter((f) => f && f.key && !f.hide);
    },
  },
  async created() {
    await this.loadRows();
  },
  methods: {
    async loadRows() {
      const raw = Array.isArray(this.value) ? this.value : this.value ? [this.value] : [];
      // Si el item trae solo ids, las filas están en la subcolección del grupo.
      const needsFetch = raw.some((r) => typeof r === "string");
      if (!needsFetch) {
        this.rows = raw.map((r) => ({ ...r }));
        this.$emit("input", this.rows);
        return;
      }
      this.loading = true;
      try {
        const groupModel = `${this.modelName}_${this.field.key}`;
        const loaded = await Promise.all(
          raw.map((r) =>
            typeof r === "string"
              ? wencesApi
                  .get(`/api/gotrading/dynamic/${encodeURIComponent(groupModel)}/${encodeURIComponent(r)}`)
                  .then((res) => res.data)
                  .catch(() => ({ _id: r }))
              : Promise.resolve(r)
          )
        );
        this.rows = loaded.map((r) => ({ ...r }));
        this.$emit("input", this.rows);
      } finally {
        this.loading = false;
      }
    },
    cellComponent(sub) {
      return CELL_COMPONENTS[sub.type] || null;
    },
    setCell(idx, key, val) {
      this.$set(this.rows[idx], key, val);
      this.$emit("input", this.rows);
    },
    addRow() {
      tmpCounter += 1;
      this.rows.push({ _tmpKey: `new-${tmpCounter}` });
      this.$emit("input", this.rows);
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll("tbody tr:last-child input");
        if (inputs[0]) inputs[0].focus();
      });
    },
    removeRow(idx) {
      this.rows.splice(idx, 1);
      this.$emit("input", this.rows);
    },
  },
};
</script>

<style scoped>
.ip-group-editor__table th {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
  border-bottom-width: 1px;
  white-space: nowrap;
}

.ip-group-editor__table td {
  min-width: 9rem;
  padding: 0.25rem 0.35rem;
}

.ip-group-editor__table td.ip-group-editor__actions,
.ip-group-editor__table th.ip-group-editor__actions {
  min-width: 0;
  width: 1%;
}

.ip-group-editor__table :deep(.form-control) {
  min-height: 0;
}

/* El scroll horizontal recorta también hacia abajo: con un desplegable abierto
   (buscador de embarque, proforma, gasto…) la lista quedaba cortada. */
.ip-group-editor .table-responsive:has(.multiselect--active),
.ip-group-editor .table-responsive:has(.dropdown-menu.show) {
  overflow: visible;
}

#darkdesign .ip-group-editor__table th {
  color: var(--dark-text-secondary);
}
</style>
